import { useState, useEffect } from 'react';
import {
  Users,
  RefreshCw,
  Search,
  Wifi,
  WifiOff,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Crown,
  TrendingUp,
  Brain,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { collection, getDocs, orderBy, query, Timestamp } from 'firebase/firestore';

const db = getFirebaseFirestore();

interface TenantData {
  uid: string;
  email: string;
  displayName?: string;
  createdAt?: Date;
  subscription?: {
    plan?: string;
    isSubscribed?: boolean;
    isTrialActive?: boolean;
    trialEndDate?: Date;
  };
  connectedPlatforms: string[];
  lastSync?: Date | null;
  aiUsageToday?: number;
  notificationsCount?: number;
}

const PLAN_COLORS: Record<string, string> = {
  enterprise: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  professional: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  growth: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  starter: 'bg-green-500/20 text-green-400 border-green-500/30',
  free: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const PLAN_LABELS: Record<string, string> = {
  enterprise: 'Enterprise',
  professional: 'Professional',
  growth: 'Growth',
  starter: 'Starter',
  free: 'Ücretsiz',
};

export function AdminTenants() {
  const [tenants, setTenants] = useState<TenantData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'createdAt' | 'platforms' | 'plan'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filterPlan, setFilterPlan] = useState<string>('all');

  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      const usersSnap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));

      const data: TenantData[] = await Promise.all(
        usersSnap.docs.map(async (doc) => {
          const d = doc.data();

          // Bağlı platformları çek (birden fazla subcollection olabilir)
          const platforms: string[] = [];
          const connectionTypes = [
            'connections', 'trendyol_connections', 'hepsiburada_connections',
            'google_connections', 'meta_connections',
          ];
          for (const col of connectionTypes) {
            try {
              const snap = await getDocs(collection(db, 'users', doc.id, col));
              snap.docs.forEach(c => {
                const cd = c.data();
                if (cd.status === 'active' || cd.platform) {
                  platforms.push(cd.platform || col.replace('_connections', ''));
                }
              });
            } catch { /* subcollection yoksa geç */ }
          }

          // Son AI kullanımı
          const today = new Date().toISOString().slice(0, 10);
          let aiUsageToday = 0;
          try {
            const usageSnap = await getDocs(
              collection(db, 'ai_usage_stats', doc.id, 'daily')
            );
            const todayDoc = usageSnap.docs.find(d => d.id === today);
            aiUsageToday = todayDoc?.data()?.requestCount || 0;
          } catch { /* yok */ }

          // Bildirim sayısı
          let notificationsCount = 0;
          try {
            const notifSnap = await getDocs(collection(db, 'users', doc.id, 'notifications'));
            notificationsCount = notifSnap.docs.filter(n => !n.data().read).length;
          } catch { /* yok */ }

          const sub = d.subscription || {};
          const trialEndDate = sub.trialEndDate instanceof Timestamp
            ? sub.trialEndDate.toDate()
            : sub.trialEndDate ? new Date(sub.trialEndDate) : undefined;

          return {
            uid: doc.id,
            email: d.email || d.displayName || doc.id,
            displayName: d.displayName,
            createdAt: d.createdAt instanceof Timestamp ? d.createdAt.toDate() : undefined,
            subscription: {
              plan: sub.plan || 'free',
              isSubscribed: !!sub.isSubscribed,
              isTrialActive: !!sub.isTrialActive,
              trialEndDate,
            },
            connectedPlatforms: [...new Set(platforms)],
            aiUsageToday,
            notificationsCount,
          };
        })
      );

      setTenants(data);
    } catch (err) {
      console.error('Tenant verisi yüklenemedi:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const filtered = tenants
    .filter(t => {
      const q = search.toLowerCase();
      return t.email.toLowerCase().includes(q) || (t.displayName || '').toLowerCase().includes(q);
    })
    .filter(t => filterPlan === 'all' || t.subscription?.plan === filterPlan)
    .sort((a, b) => {
      let va: number | string, vb: number | string;
      if (sortBy === 'platforms') { va = a.connectedPlatforms.length; vb = b.connectedPlatforms.length; }
      else if (sortBy === 'plan') { va = a.subscription?.plan || ''; vb = b.subscription?.plan || ''; }
      else { va = a.createdAt?.getTime() || 0; vb = b.createdAt?.getTime() || 0; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const stats = {
    total: tenants.length,
    subscribed: tenants.filter(t => t.subscription?.isSubscribed).length,
    trial: tenants.filter(t => t.subscription?.isTrialActive).length,
    withPlatforms: tenants.filter(t => t.connectedPlatforms.length > 0).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-purple-400" />
            Tenant Yönetimi
          </h1>
          <p className="text-gray-400 mt-1">Tüm müşterilerin platform ve AI kullanım durumu</p>
        </div>
        <button
          onClick={fetchTenants}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-gray-400 hover:text-white transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Kullanıcı', value: stats.total, icon: Users, color: 'purple' },
          { label: 'Aktif Abonelik', value: stats.subscribed, icon: Crown, color: 'amber' },
          { label: 'Deneme Süresi', value: stats.trial, icon: Clock, color: 'blue' },
          { label: 'Platform Bağlı', value: stats.withPlatforms, icon: Wifi, color: 'green' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-[#1a1a2e]/50 rounded-2xl p-4 border border-white/5">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-${color}-400`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{value}</p>
                <p className="text-xs text-gray-400">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="E-posta veya isim ara..."
            className="w-full pl-9 pr-4 py-2.5 bg-[#1a1a2e] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 text-sm"
          />
        </div>
        <select
          value={filterPlan}
          onChange={e => setFilterPlan(e.target.value)}
          className="px-4 py-2.5 bg-[#1a1a2e] border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-purple-500/50"
        >
          <option value="all">Tüm Planlar</option>
          {Object.entries(PLAN_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[#1a1a2e]/50 rounded-2xl border border-white/5 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 text-xs text-gray-500 font-medium">
          <div className="col-span-3">Kullanıcı</div>
          <button
            className="col-span-2 flex items-center gap-1 hover:text-gray-300"
            onClick={() => toggleSort('plan')}
          >
            Plan {sortBy === 'plan' ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
          </button>
          <button
            className="col-span-2 flex items-center gap-1 hover:text-gray-300"
            onClick={() => toggleSort('platforms')}
          >
            Platformlar {sortBy === 'platforms' ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
          </button>
          <div className="col-span-2">AI Kullanım</div>
          <div className="col-span-2">Bildirimler</div>
          <button
            className="col-span-1 flex items-center gap-1 hover:text-gray-300"
            onClick={() => toggleSort('createdAt')}
          >
            Kayıt {sortBy === 'createdAt' ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <Users className="w-12 h-12 mb-3 opacity-30" />
            <p>Kullanıcı bulunamadı</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(tenant => {
              const plan = tenant.subscription?.plan || 'free';
              const isActive = tenant.subscription?.isSubscribed;
              const isTrial = tenant.subscription?.isTrialActive;
              return (
                <div key={tenant.uid} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-white/5 transition-colors">
                  {/* Kullanıcı */}
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm text-white truncate">{tenant.displayName || tenant.email}</p>
                    {tenant.displayName && (
                      <p className="text-xs text-gray-500 truncate">{tenant.email}</p>
                    )}
                  </div>

                  {/* Plan */}
                  <div className="col-span-2">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full border ${PLAN_COLORS[plan] || PLAN_COLORS.free}`}>
                        {PLAN_LABELS[plan] || plan}
                      </span>
                      {isActive && (
                        <span className="flex items-center gap-1 text-xs text-green-400">
                          <CheckCircle2 className="w-3 h-3" /> Aktif
                        </span>
                      )}
                      {!isActive && isTrial && (
                        <span className="flex items-center gap-1 text-xs text-blue-400">
                          <Clock className="w-3 h-3" /> Deneme
                        </span>
                      )}
                      {!isActive && !isTrial && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <AlertTriangle className="w-3 h-3" /> Pasif
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Platformlar */}
                  <div className="col-span-2">
                    {tenant.connectedPlatforms.length > 0 ? (
                      <div className="flex items-center gap-1.5">
                        <Wifi className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm text-white">{tenant.connectedPlatforms.length}</span>
                        <span className="text-xs text-gray-500 truncate">
                          {tenant.connectedPlatforms.slice(0, 2).join(', ')}
                          {tenant.connectedPlatforms.length > 2 && ` +${tenant.connectedPlatforms.length - 2}`}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <WifiOff className="w-4 h-4" />
                        <span className="text-xs">Yok</span>
                      </div>
                    )}
                  </div>

                  {/* AI Kullanım */}
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5">
                      <Brain className="w-4 h-4 text-purple-400" />
                      <span className={`text-sm ${(tenant.aiUsageToday ?? 0) > 0 ? 'text-white' : 'text-gray-600'}`}>
                        {(tenant.aiUsageToday ?? 0) > 0 ? `${tenant.aiUsageToday} istek` : '—'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">bugün</p>
                  </div>

                  {/* Bildirimler */}
                  <div className="col-span-2">
                    {tenant.notificationsCount && tenant.notificationsCount > 0 ? (
                      <span className="flex items-center gap-1.5 text-sm text-amber-400">
                        <AlertTriangle className="w-4 h-4" />
                        {tenant.notificationsCount} okunmamış
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600">—</span>
                    )}
                  </div>

                  {/* Kayıt Tarihi */}
                  <div className="col-span-1">
                    <p className="text-xs text-gray-500">
                      {tenant.createdAt
                        ? tenant.createdAt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
                        : '—'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Summary */}
      {!isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <TrendingUp className="w-4 h-4" />
          {filtered.length} / {tenants.length} kullanıcı gösteriliyor
        </div>
      )}
    </div>
  );
}
